<template>
  <NuxtLayout
    name="generic"
    header="standardDark"
    footer="standard"
  >
    <div
      v-if="data"
      :class="{
        'home-page-wrapper': true,
        'mt-100px pb-100px md:mt-40 lg:mt-36': isStartScreen,
      }"
    >
      <!-- Hero and sections shown only on start screen -->
      <HomePageHero
        v-show="isStartScreen"
        v-bind="data"
      />
      <SectionBuilder
        v-if="data.homepageSectionBuilder"
        v-show="isStartScreen"
        v-bind="data.homepageSectionBuilder"
        :class="{ 'text-www-color-neutral-medium-dark': true }"
      />
    </div>
  </NuxtLayout>
</template>

<script lang="ts" setup>
import HomePageHero from '~/components/HomePage/HomePageHero/HomePageHero.vue';
import { queryHome } from '~/queries/pages/PageHome.query';
import type { QueryHomeResult } from '~/sanity/types';
import SectionBuilder from '~/components/Atmos/SectionBuilder/SectionBuilder.vue';
import { STRUCTURED_DATA_DEFAULTS } from '~/utils/structuredDataDefaults';

const isStartScreen = useState('uss-is-start-screen', () => true);

const { data } = await useWwwSanityQueryNonNull<QueryHomeResult>(queryHome);
const organizationId = STRUCTURED_DATA_DEFAULTS.organization.id;

usePageHead({
  _id: data.value?._id,
  description: data.value?.ogDescription ?? data.value?.description,
  ogImage:
    data.value?.ogImage?.asset || data.value?.cardImage?.asset || data.value?.heroImage?.asset,
  title: data.value?.htmlTitle ?? data.value?.title,
  excludeFromExternalSearch: data.value?.excludeFromExternalSearch,
  twitterCard: 'summary',
  structuredData: [
    {
      schemaType: 'WebSite',
      description: data.value?.ogDescription ?? data.value?.description,
      organizationId,
      enabled: true,
    },
    {
      schemaType: 'Organization',
      organizationId,
      description: data.value?.ogDescription ?? data.value?.description,
      enabled: true,
    },
  ],
});
</script>
<style lang="scss">
.home-page-header {
  .text-one-column {
    // margin-bottom: 2rem;
    h5 {
      font-family: Rajdhani !important;
      font-size: 1.3125rem !important;
      font-weight: 600 !important;
      line-height: 2rem !important;
      padding-bottom: 1.5625rem !important;
    }
    h1 {
      font-size: 3.125rem !important;
      font-weight: 800 !important;
      line-height: 3.875rem !important;
      padding-bottom: 1.5625rem !important;
      margin-bottom: 0 !important;
    }
    p.content {
      display: inline !important;
    }
  }
  .hekate-form-fort-container form {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 0 !important;
    margin: 0 0 1.25rem !important;
    input {
      margin-bottom: 0 !important;
      &::placeholder {
        text-align: left !important;
      }
    }
    .signup-next-button {
      background-color: var(--www-color-primary-light);
      border: 3px solid var(--www-color-primary-light);
      color: #fff;
      border-radius: 0.25rem;
      padding: 0.9375rem 1rem;
      height: 100%;
      font-family: Rajdhani, Arial, sans-serif;
      font-size: 18px;
      line-height: 1.125rem;
      font-weight: 700;
      letter-spacing: 0.5px;
      text-align: center;
      text-transform: uppercase;
      margin: 0 0 0 1rem !important;
      @media (max-width: 567px) {
        margin: 1rem 0 0 0rem !important;
      }
      &.signup-next-button-disabled {
        cursor: not-allowed;
        opacity: 0.5;
      }
    }
  }
  @media (max-width: 1024px) {
    .button-wrapper {
      justify-content: center !important;
      text-align: center !important;
    }
  }
}

.home-page-header .text-one-column p {
  font-size: 1.5rem !important;
  font-weight: 600 !important;
  line-height: 1.875rem !important;
  color: var(--www-color-neutral-medium);
}
.home-page-header .form-container .bg-wrapper {
  padding: 0 !important;
}
.home-page-header .bg-wrapper {
  width: 100% !important;
}

.home-page-wrapper .callouts-with-icon h4 {
  font-size: 2.5rem !important;
  line-height: 3.125rem !important;
  font-weight: 800 !important;
}
.home-page-wrapper .callouts-with-icon .card-content h5 {
  font-size: 1.125rem !important;
  font-weight: 700 !important;
  line-height: 1.4375rem !important;
  margin-bottom: 1rem !important;
  text-transform: uppercase !important;
}

.home-page-header .form-container .text-one-column p {
  font-size: 21px !important;
  font-weight: 600 !important;
  line-height: 2rem !important;
  padding-left: 0 !important;
  color: var(--www-color-neutral-medium-dark);
}
@media (max-width: 600px) {
  .home-page-header .form-container .text-one-column p {
    font-size: 1rem !important;
    line-height: 1.5rem !important;
  }
}
@media (max-width: 575px) {
  .home-page-wrapper .callouts-with-icon h4 {
    font-size: 1.5rem !important;
    padding: 0 1.25rem !important;
  }
}
@media (max-width: 767px) {
  .home-page-wrapper .callouts-with-icon h4 {
    font-size: 1.75rem !important;
    font-weight: 800 !important;
    line-height: 2.5rem !important;
    margin: 0 auto 4.0625rem !important;
    max-width: 38.75rem !important;
  }
}
@media (max-width: 575px) {
  .home-page-wrapper .callouts-with-icon h4 {
    font-size: 1.5rem !important;
    padding: 0 !important;
  }
}
@media (max-width: 768px) {
  .home-page-wrapper .section-builder-button-row .bg-wrapper .button-wrapper a {
    width: auto !important;
    line-height: 1.5rem !important;
    padding: 8px 16px !important;
  }
}

@media (max-width: 1024px) {
  .home-page-wrapper .section-builder-callout .background-image-wrapper {
    background-image: unset !important;
  }
}
</style>
